<template>
  <div>
    <template v-if="payment.recipientEvent">
      <div class="mb-2">
        <AppShowEventDetails :event="payment.recipientEvent" link small />
      </div>

      <EntryAndProducts :payables="payment.payables" />
    </template>
    <template v-if="payment.invoice">
      <div class="d-flex">
        <div>
          {{
            $t(
              "people:ShoppingBasket.TransactionHistory.invoicePayment.label",
              "Invoice:"
            )
          }}
          {{ payment.invoice.invoiceNumberToDisplay }}
          <br />
          <AppShowOrganisationName
            class="muted--text"
            :organisation="payment.invoice.sellerOrganisation"
          />
        </div>
        <AppShowInvoicePdfs
          :invoice="payment.invoice"
          :shopping-basket-id="shoppingBasketId"
          class="ml-5"
        />
      </div>
      <br />

      <AppShowInvoiceLineDetails
        v-for="invoiceLine in payment.invoice.invoiceLines"
        :key="invoiceLine.id"
        :invoice-line="invoiceLine"
      />
    </template>
    <template v-else>
      <div v-for="key in groupedPayablesKeys" :key="key">
        <template v-if="key == 'HORSE_SEASON_SPORT_LICENCE'">
          <b>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.HorseSportLicencepayment.label",
                "Season sport licence(s) for horse(s):",
                "Label for Horse sprot licence(s) list in Transaction History tab for transaction"
              )
            }}
          </b>
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <AppShowHorseName :horse="payable.horse" />
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template v-if="key == 'HORSE_SEASON_FEI_LICENCE'">
          <b>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.HorseFEILicencepayment.label",
                "Season FEI licence(s) for horse(s):"
              )
            }}
          </b>
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <AppShowHorseName
              :horse="payable.horse || payable.horseApplication.horse"
            />
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template v-else-if="key == 'HORSE_SEASON_SPORT_LICENCE_BUNDLE'">
          <b>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.SportLicenceBundle.label",
                "Sport licence bundle",
                "Label for sport licence bundle in Transaction History tab for transaction"
              )
            }}
          </b>
          <br />
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <AppOrganisationNameLink :organisation="payable.organisation" />
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template v-else-if="key == 'HORSE_TEMPORARY_SPORT_LICENCE'">
          <b>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.HorseTemporaryLicencepayment.label",
                "Temporary sport licence(s) for horse(s):",
                "Label for Horse temporary licence(s) list in Transaction History tab for transaction"
              )
            }}
          </b>
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <AppShowHorseName :horse="payable.horse" />
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template v-else-if="key == 'HORSE_LIFETIME_SPORT_LICENCE'">
          <b>
            {{
              $t(
                "people:ShoppingBasket.TransactionHistory.HorseLifetimeSportLicencepayment.label",
                "Lifetime sport licence(s) for horse(s):"
              )
            }}
          </b>
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <AppShowHorseName :horse="payable.horse" />
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template
          v-else-if="
            [
              'HORSE_APPLICATION_REGISTER',
              'HORSE_APPLICATION_PONY_MEASUREMENT',
              'HORSE_APPLICATION_OWNERSHIP_CHANGE',
              'HORSE_APPLICATION_OWNERSHIP_CHANGE_WITH_REGISTRATION_FEE'
            ].includes(key)
          "
        >
          <b>
            {{
              $t(
                "wallet:horseRegistrationApplication.reg_application_fee.label"
              )
            }}
          </b>
          <div
            v-for="payable in groupedPayables[key]"
            :key="payable.id"
            class="d-flex flex-row"
          >
            <div>
              <div
                v-if="
                  payable.horseApplication && payable.horseApplication.horse
                "
              >
                <template v-if="payable.horseApplication.horse.id">
                  <AppShowHorseName :horse="payable.horseApplication.horse" />
                </template>
                <template v-else>
                  {{ payable.horseApplication.horse.name }}
                </template>
              </div>
              <div v-else-if="payable.horseApplication">
                {{ payable.horseApplication.horseBirthName }}
              </div>
              <div v-else>?</div>
            </div>
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
          <br />
        </template>
        <template
          v-else-if="
            [
              'FEI_PASSPORT_FOR_HORSE',
              'FEI_PASSPORT_FOR_PONY',
              'FEI_PASSPORT_RENEWAL_FOR_HORSE',
              'FEI_PASSPORT_RENEWAL_FOR_PONY',
              'FEI_REGISTER_ID'
            ].includes(key)
          "
        >
          <b>{{ $t("common:product.codename." + key) }}</b>
          <div v-for="payable in groupedPayables[key]" :key="payable.id">
            <div
              v-if="payable.horseApplication && payable.horseApplication.horse"
              class="d-flex"
            >
              <AppShowHorseName :horse="payable.horseApplication.horse" />
              <AppShowPaymentStatus
                v-if="payable.status == 'refunded'"
                :value="payable"
                class-list="ml-2"
              />
            </div>
          </div>

          <br />
        </template>
      </div>

      <div v-for="payable in basicPayables" :key="payable.id + 'baisc'">
        <div class="d-flex">
          <strong>{{ payable.product.name }}</strong>
          <AppShowPaymentStatus
            v-if="payable.status == 'refunded'"
            :value="payable"
            class-list="ml-2"
          />
        </div>

        <!-- <AppShowEventDetails
          v-if="
            payable.product.relatedEntities.includes('Event') && payable.event
          "
          :event="payable.entry"
          class="mb-2"
        />-->

        <AppShowEntryDetailsLine
          v-if="
            payable.product.relatedEntities.includes('Entry') && payable.entry
          "
          :entry="payable.entry"
          class="mb-2"
        />

        <!-- 
        <AppShowPersonNameWithIdentifiers
          v-if="
            payable.product.relatedEntities.includes('Person') && payable.person
          "
          :person="payable.person"
          class="mb-2"
        />-->

        <AppShowHorseName
          v-if="
            payable.product.relatedEntities.includes('Horse') && payable.horse
          "
          :horse="payable.horse"
          class="mb-2"
          show-identifier
        />
      </div>

      <div v-for="payable in payables" :key="payable.id" class="mb-2">
        <div v-if="payable.productId">
          <div class="d-flex">
            <strong>{{ payable.product.name }}</strong>
            <AppShowPaymentStatus
              v-if="payable.status == 'refunded'"
              :value="payable"
              class-list="ml-2"
            />
          </div>
        </div>

        <AppShowEntryDetailsLine
          v-if="
            payable.product.relatedEntities.includes('Entry') && payable.entry
          "
          :entry="payable.entry"
          class="mb-2"
        />

        <AppShowInputSkeleton
          v-if="payable.productParams"
          order-preset="productParams"
          :params="payable.productParams"
        />
      </div>
    </template>
  </div>
</template>
<script>
import { defineComponent, computed } from "vue";
import { groupBy } from "lodash-es";
import gql from "fraql";

import AppShowInputSkeleton from "@AppShows/AppShowInputSkeleton.vue";
import AppShowEventDetails from "@/components/elements/AppShows/event/AppShowEventDetails.vue";
import AppOrganisationNameLink from "@/components/elements/AppNameLinks/AppOrganisationNameLink.vue";
import AppShowHorseName from "@AppShows/horse/AppShowHorseName.vue";
import AppShowPaymentStatus from "@AppShows/order/AppShowPaymentStatus.vue";

import AppShowInvoiceLineDetails from "@AppShows/AppShowInvoiceLineDetails.vue";
import AppShowInvoicePdfs from "@AppShows/AppShowInvoicePdfs.vue";
import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";
import AppShowEntryDetailsLine from "@AppShows/event/AppShowEntryDetailsLine.vue";
import AppShowPersonNameWithIdentifiers from "@AppShows/person/AppShowPersonNameWithIdentifiers.vue";

import EntryAndProducts from "./EntryAndProducts.vue";

export default defineComponent({
  name: "PaymentHistoryDetails",
  components: {
    AppShowEventDetails,
    AppOrganisationNameLink,
    AppShowHorseName,
    AppShowInputSkeleton,
    AppShowPaymentStatus,
    AppShowInvoiceLineDetails,
    AppShowInvoicePdfs,
    AppShowOrganisationName,
    AppShowEntryDetailsLine,
    EntryAndProducts
  },
  inject: {
    shoppingBasketId: {
      default: null
    }
  },
  props: {
    payment: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const groupedPayables = computed(() => {
      return groupBy(props.payment.payables, "productCodename");
    });
    const groupedPayablesKeys = computed(() => {
      return Object.keys(groupedPayables.value);
    });

    const payables = computed(() => {
      return props.payment.payables
        .filter(x => x.productParams)
        .filter(
          x =>
            !x.productCodename ||
            (!x.productCodename.includes("HORSE") &&
              !x.productCodename.includes("PONY"))
        );
    });

    const basicPayables = computed(() => {
      return props.payment.payables.filter(
        x => !x.productCodename && x.horseId
      );
    });

    return {
      groupedPayables,
      groupedPayablesKeys,

      payables,
      basicPayables
    };
  },
  fragments: {
    productFields: gql`
      fragment _ on Product {
        name
        relatedEntities
      }
    `,
    paymentFields: gql`
      fragment _ on Payment {
        id
        recipientEvent {
          ${AppShowEventDetails.fragments.event}
        }
      }
    `,
    payableFields: gql`
      fragment _ on Payable {
        eventId
        event {
          id
          ${AppShowEventDetails.fragments.event}
        }
        personId
        person {
          id
          ${AppShowPersonNameWithIdentifiers.fragments.person}
        }
        horseId
        horse {
          id
          ${AppShowHorseName.fragments.horse}
        }
        entryId
        entry {
          id
          ${AppShowEntryDetailsLine.fragments.entryFields}
        }
        ${EntryAndProducts.fragments.payableFields}
      }
    `,
    invoiceFields: gql`
      fragment _ on Invoice {
        id
        invoiceNumberToDisplay
        invoiceNumber
        ${AppShowInvoicePdfs.fragments.invoiceFields}
        sellerOrganisation {
          id
          ${AppShowOrganisationName.fragments.organisation}
        }
        invoiceLines {
          id
          ${AppShowInvoiceLineDetails.fragments.invoiceLineFields}
          entry {
            id
            ${AppShowInvoiceLineDetails.fragments.entryFields}
          }
          horse {
            id
            ${AppShowInvoiceLineDetails.fragments.horseFields}
          }
        }
      }
    `
  }
});
</script>
